import {
    ChangeDetectionStrategy,
    Component,
    effect,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Renderer2,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { TopbarComponent } from './components/topbar/topbar.component';
import { MenuService } from './services/menu.service';
import { LayoutService } from './services/layout.service';
import { getUserSettings, setBackendUrl } from './store/layout.actions';
import { SubscriptionComponent } from '../shared/helpers/subscription.component';
import { LayoutStore } from './layout.store';
import { getAllowStatisticsFrag } from './layout.query';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { BreadcrumbComponent } from './components/breadcrumbs/breadcrumb.component';
import { UpdateComponent } from './components/update/update.component';
import { FooterComponent } from './components/footer/footer.component';
import { SearchComponent } from './components/search/search.component';
import { ServiceTypeEnum } from '@libs/types/src/services/enum/service-type.enum';
import { BackgroundTaskProgressComponent } from './components/background-task-progress/background-task-progress.component';

@Component({
    selector: 'app-layout',
    templateUrl: './layout.component.html',
    providers: [LayoutStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        NgClass,
        TopbarComponent,
        BreadcrumbComponent,
        UpdateComponent,
        NgTemplateOutlet,
        FooterComponent,
        SearchComponent,
        BackgroundTaskProgressComponent,
    ],
})
export class LayoutComponent extends SubscriptionComponent implements OnDestroy, OnInit, OnChanges {

    private menuService = inject(MenuService);
    public layoutService = inject(LayoutService);
    public renderer = inject(Renderer2);
    public router = inject(Router);
    private store = inject(Store);
    private store2 = inject(LayoutStore);
    @ViewChild(TopbarComponent) appTopbar!: TopbarComponent;
    @Input() backendUrl = '';

    overlayMenuOpenSubscription: Subscription;
    menuOutsideClickListener: (() => void) | null = null;

    constructor() {
        super();

        this.overlayMenuOpenSubscription =
            this.layoutService.overlayOpen$.subscribe(() => {
                if (!this.menuOutsideClickListener) {
                    this.menuOutsideClickListener = this.renderer.listen(
                        'document',
                        'click',
                        (event) => {
                            const isOutsideClicked = !(
                                this.appTopbar?.appSidebar.el.nativeElement.isSameNode(
                                    event.target,
                                ) ||
                                this.appTopbar?.appSidebar.el.nativeElement.contains(
                                    event.target,
                                ) ||
                                this.appTopbar?.menuButton.nativeElement.isSameNode(
                                    event.target,
                                ) ||
                                this.appTopbar?.menuButton.nativeElement.contains(
                                    event.target,
                                )
                            );
                            if (isOutsideClicked) {
                                this.hideMenu();
                            }
                        },
                    );
                }

                if (this.layoutService.state.staticMenuMobileActive) {
                    this.blockBodyScroll();
                }
            });

        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe(() => {
                this.hideMenu();
                this.unblockBodyScroll();
            });

        effect(() => {
            const activationCount = this.layoutService.submenuContentActivationCount();
            if (!activationCount) return;

            this.hideMenu();
        });

        effect(async () => {
            const services = this.store2.globalGlobalStore.organizationStore.services();
            if (!this.store2.isAllowed('folder.read')() || services.length === 0) {
                this.store2.globalGlobalStore.medicalServiceStore.updateAllowStatistics([]);
                this.store2.globalGlobalStore.socialServiceStore.updateAllowStatistics([]);
                return;
            }
            await this.refreshAllowStatistics();
        });

        this.store2.onSuccess(
            () => this.refreshAllowStatistics(),
            'upsertServiceAllowStatistics',
        );
    }

    get containerClass() {
        const config = this.layoutService.config();

        return {
            'layout-overlay': config.menuType === 'overlay',
            'layout-static':
                config.menuType === 'static' &&
                this.layoutService.submenuContent === null,
            'layout-slim':
                config.menuType === 'slim' &&
                this.layoutService.submenuContent === null,
            'layout-horizontal':
                config.menuType === 'horizontal',
            'layout-compact':
                config.menuType === 'compact' || (!['overlay', 'horizontal'].includes(config.menuType) && this.layoutService.submenuContent !== null),
            'layout-sidebar-dark':
                config.colorScheme === 'dark',
            'layout-overlay-active':
                this.layoutService.state.overlayMenuActive ||
                this.layoutService.state.staticMenuMobileActive,
            'layout-mobile-active':
            this.layoutService.state.staticMenuMobileActive,
            'layout-static-inactive':
                this.layoutService.state.staticMenuDesktopInactive && config.menuType === 'static',
            'p-ripple-disabled': !config.ripple,
            'p-input-filled': config.inputStyle === 'filled',
        };
    }

    ngOnInit(): void {
        this.store.dispatch(getUserSettings());
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.store.dispatch(
            setBackendUrl({
                backendUrl: changes['backendUrl'].currentValue,
            }),
        );
    }

    blockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.add('blocked-scroll');
        } else {
            document.body.className += ' blocked-scroll';
        }
    }

    unblockBodyScroll(): void {
        if (document.body.classList) {
            document.body.classList.remove('blocked-scroll');
        } else {
            document.body.className = document.body.className.replace(
                new RegExp(
                    `(^|\\b)${'blocked-scroll'.split(' ').join('|')}(\\b|$)`,
                    'gi',
                ),
                ' ',
            );
        }
    }

    hideMenu(): void {
        this.layoutService.state.overlayMenuActive = false;
        this.layoutService.state.staticMenuMobileActive = false;
        this.layoutService.state.menuHoverActive = false;
        this.menuService.reset();
        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
            this.menuOutsideClickListener = null;
        }
        this.unblockBodyScroll();
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        if (this.overlayMenuOpenSubscription) {
            this.overlayMenuOpenSubscription.unsubscribe();
        }

        if (this.menuOutsideClickListener) {
            this.menuOutsideClickListener();
        }
    }

    private async refreshAllowStatistics(): Promise<void> {
        const res = await this.store2.api.getServiceAllowStatistics({ serviceId: null }, getAllowStatisticsFrag);
        const services = this.store2.globalGlobalStore.organizationStore.services();
        const medicalServiceIds = new Set(
            services
                .filter((service) => service.type === ServiceTypeEnum.HEALTH_CARE)
                .map((service) => service.id),
        );
        const socialServiceIds = new Set(
            services
                .filter((service) => service.type === ServiceTypeEnum.SOCIAL)
                .map((service) => service.id),
        );
        const settings = res || [];

        this.store2.globalGlobalStore.medicalServiceStore.updateAllowStatistics(
            settings.filter((setting) => medicalServiceIds.has(setting.serviceId)),
        );
        this.store2.globalGlobalStore.socialServiceStore.updateAllowStatistics(
            settings.filter((setting) => socialServiceIds.has(setting.serviceId)),
        );
    }

}
